/**
 * AssetRadar — authentication context.
 *
 * The opaque session cookie (`craw_session`, HttpOnly) is the ONLY source of
 * truth. Nothing is mirrored into localStorage or sessionStorage: a copy could
 * outlive the server-side session and would produce a UI that claims to be
 * logged in while every request 401s.
 *
 * `AuthUser.permissions` comes from the server and is trusted over any local
 * derivation (`permissionsForRoles` exists for the admin matrix view only).
 * Unknown permission strings — a backend that grew a new one — are filtered out
 * with `isPermission` instead of crashing the app.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  ApiError,
  changePassword as apiChangePassword,
  errorMessage,
  fetchCurrentUser,
  login as apiLogin,
  logout as apiLogout,
  setUnauthorizedHandler,
} from './api';
import { isPermission, isRole } from './types';
import type {
  AuthState,
  AuthUser,
  ChangePasswordRequest,
  LoginRequest,
  Permission,
  Role,
} from './types';

/**
 * What `useAuth()` returns. Mirrors the frozen `AuthState` contract and adds
 * `isAdmin`. `login` accepts either the wire body or the two plain arguments,
 * so both `login({email, password})` and `login(email, password)` are valid.
 */
export interface AuthContextValue extends Omit<AuthState, 'login'> {
  login(body: LoginRequest): Promise<void>;
  login(email: string, password: string): Promise<void>;
  /** True when the user holds the 'admin' role. Prefer `has(permission)`. */
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Drop anything the server sent that this build does not know about. */
function sanitiseUser(user: AuthUser): AuthUser {
  const roles: Role[] = (user.roles ?? []).filter((role): role is Role => isRole(role));
  const permissions: Permission[] = (user.permissions ?? []).filter((perm): perm is Permission =>
    isPermission(perm),
  );
  return { ...user, roles, permissions };
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Guards against a late in-flight probe overwriting a newer result.
  const requestSeq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const me = await fetchCurrentUser();
      if (seq !== requestSeq.current) return;
      setUser(sanitiseUser(me));
      setError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setUser(null);
      // A plain 401 is the normal unauthenticated case, never an error banner.
      setError(err instanceof ApiError && err.status === 401 ? null : errorMessage(err));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Any 401 from any endpoint means the session is gone: drop the user so the
  // router falls back to the login screen. api.ts calls this hook for us.
  useEffect(() => {
    const unsubscribe = setUnauthorizedHandler(() => {
      requestSeq.current += 1;
      setUser(null);
      setLoading(false);
      setError(null);
    });
    return unsubscribe;
  }, []);

  const login = useCallback(
    async (bodyOrEmail: LoginRequest | string, maybePassword?: string): Promise<void> => {
      const body: LoginRequest =
        typeof bodyOrEmail === 'string'
          ? { email: bodyOrEmail, password: maybePassword ?? '' }
          : bodyOrEmail;
      const seq = ++requestSeq.current;
      const response = await apiLogin(body);
      if (seq !== requestSeq.current) return;
      setUser(sanitiseUser(response.user));
      setError(null);
      setLoading(false);
    },
    [],
  );

  const logout = useCallback(async (): Promise<void> => {
    requestSeq.current += 1;
    try {
      await apiLogout();
    } finally {
      // Even a failed logout must clear the client state — the cookie may
      // already be invalid, and keeping the user would strand the UI.
      setUser(null);
      setError(null);
      setLoading(false);
    }
  }, []);

  const changePassword = useCallback(
    async (body: ChangePasswordRequest): Promise<void> => {
      await apiChangePassword(body);
      // must_change_password flips server-side; re-read the authoritative user.
      await load();
    },
    [load],
  );

  const value = useMemo<AuthContextValue>(() => {
    const granted = new Set<Permission>(user?.permissions ?? []);
    const has = (permission: Permission): boolean => granted.has(permission);
    return {
      user,
      loading,
      error,
      login,
      logout,
      refresh: load,
      changePassword,
      has,
      hasAny: (...permissions: Permission[]) => permissions.some(has),
      isAdmin: (user?.roles ?? []).includes('admin'),
    };
  }, [user, loading, error, login, logout, load, changePassword]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Access the auth context. Throws when used outside `AuthProvider`. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth() muss innerhalb von <AuthProvider> verwendet werden.');
  }
  return ctx;
}
